import { useState, useCallback } from 'react'
import {
  Maybe,
  IConfigurableBuilder,
  ITransactionSetTypes,
  ITransactionSetSubtypes,
  IConfigurableBuilderTest,
  IStupidEdiConfigs,
  IBuilderLibraries,
} from 'types/graphqlTypes'
import { useConfigurableBuildersQuery } from '../graphql/ConfigurableBuilders'
import { useCreateConfigurableBuilderMutation } from '../graphql/CreateConfigurableBuilderMutation'
import { useRunConfigurableBuilderMutation } from '../graphql/RunConfigurableBuilderMutation'
import { useUpdateConfigurableBuilderMutation } from '../graphql/UpdateConfigurableBuilderMutation'
import { IConfigurableBuilderOptionsSchema } from '../schema/ConfigurableBuilderOptionsSchema'
import { OUTBOUND_EDI_MAPPER_990 } from '../utils/constants'
import { formatForCodeEditor } from '../utils/formatting'

export const useConfigurableBuilder = (
  displayUserFeedback: (message: string, level: 'error' | 'info') => void
) => {
  const [
    selectedConfigurableBuilder,
    setSelectedConfigurableBuilder,
  ] = useState<IConfigurableBuilder | null>(null)
  const [configurableBuilderOptions, setConfigurableBuilderOptions] = useState<
    IConfigurableBuilderOptionsSchema
  >({
    outboundEdiMapperConnectionConfigId: OUTBOUND_EDI_MAPPER_990,
  })
  const [jsonResult, setJsonResult] = useState<string>('')
  const [ediResult, setEdiResult] = useState<string>('')
  const {
    data: configurableBuildersData,
    loading: isLoadingConfigurableBuilders,
    refetch: refetchConfigurableBuilders,
  } = useConfigurableBuildersQuery()

  const [
    runConfigurableBuilderMutation,
    { loading: isLoadingRunMutation },
  ] = useRunConfigurableBuilderMutation()
  const [
    createConfigurableBuilderMutation,
    { loading: isLoadingCreateMutation },
  ] = useCreateConfigurableBuilderMutation()
  const [
    updateConfigurableBuilderMutation,
    { loading: isLoadingUpdateMutation },
  ] = useUpdateConfigurableBuilderMutation()

  const createConfigurableBuilder = useCallback(
    async (
      name: string,
      transactionSet: ITransactionSetTypes,
      transactionSetSubtype?: Maybe<ITransactionSetSubtypes>
    ) => {
      try {
        const response = await createConfigurableBuilderMutation({
          variables: {
            input: {
              transactionSet,
              transactionSetSubtype,
              name,
            },
          },
        })
        let errors = response.data?.createConfigurableBuilder?.errors || []
        errors = errors.filter(item => item.message)
        if (errors.length === 0) {
          refetchConfigurableBuilders()
        } else {
          displayUserFeedback(errors[0].message, 'error')
        }
      } catch (e) {
        displayUserFeedback(e.message, 'error')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createConfigurableBuilderMutation]
  )

  const updateConfigurableBuilder = useCallback(
    async (
      id: string,
      name: Maybe<string>,
      transactionSet: Maybe<ITransactionSetTypes>,
      transactionSetSubtype: Maybe<ITransactionSetSubtypes>,
      isLive: Maybe<boolean>
    ) => {
      try {
        const response = await updateConfigurableBuilderMutation({
          variables: {
            input: {
              id,
              name,
              transactionSet,
              // needed so we can set update a subtype back to null. (undefined won't do this.)
              transactionSetSubtype: transactionSetSubtype || null,
              isLive,
            },
          },
        })
        let errors = response.data?.updateConfigurableBuilder?.errors || []
        errors = errors.filter(item => item.message)
        if (errors.length === 0) {
          refetchConfigurableBuilders()
        } else {
          displayUserFeedback(errors[0].message, 'error')
        }
      } catch (e) {
        displayUserFeedback(e.message, 'error')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateConfigurableBuilderMutation]
  )

  const runConfigurableBuilder = useCallback(
    async (
      extractionTemplate: string,
      configurableBuilderOptions: IConfigurableBuilderOptionsSchema,
      outboundInputOptions: IConfigurableBuilderTest,
      builderLibrary: IBuilderLibraries,
      stupidediConfig: Maybe<IStupidEdiConfigs>,
      tfx12DefinitionId: Maybe<string>,
      transactionSet: ITransactionSetTypes,
      transactionSetSubtype: Maybe<ITransactionSetSubtypes>
    ) => {
      setJsonResult('')
      setEdiResult('')

      try {
        const response = await runConfigurableBuilderMutation({
          variables: {
            input: {
              accountingInvoiceId: outboundInputOptions.accountingInvoiceId,
              builderLibrary,
              changedField: outboundInputOptions.changedField,
              changedShipperStopSequence: outboundInputOptions.changedShipperStopSequence,
              extractionTemplate,
              outboundEdiMapperConnectionConfigId:
                configurableBuilderOptions.outboundEdiMapperConnectionConfigId,
              stupidediConfig,
              shipmentId: outboundInputOptions.shipmentId,
              tfx12DefinitionId,
              transactionSet,
              transactionSetSubtype,
            },
          },
        })
        let errors = response.data?.runConfigurableBuilder?.errors || []
        errors = errors.filter(item => item.message)
        const jsonOutput = response.data?.runConfigurableBuilder?.jsonOutput || []
        const formattedJson = formatForCodeEditor(jsonOutput)
        const ediOutput = response.data?.runConfigurableBuilder?.ediOutput || []
        setJsonResult(formattedJson)
        setEdiResult(ediOutput.toString().replaceAll('~', '~\n')) // TODO: separator won't always be '~'
        if (errors.length !== 0) {
          displayUserFeedback(errors.map(item => item.message).join('\n'), 'error')
        }
      } catch (e) {
        displayUserFeedback(e.message, 'error')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runConfigurableBuilderMutation]
  )

  const runQuery = ({
    builderLibrary,
    extractionTemplate,
    outboundInputOptions,
    stupidediConfig,
    tfx12DefinitionId,
    transactionSet,
    transactionSetSubtype,
  }: {
    extractionTemplate: string
    outboundInputOptions: IConfigurableBuilderTest
    builderLibrary: IBuilderLibraries
    stupidediConfig: Maybe<IStupidEdiConfigs>
    tfx12DefinitionId: Maybe<string>
    transactionSet: ITransactionSetTypes
    transactionSetSubtype?: Maybe<ITransactionSetSubtypes>
  }) => {
    runConfigurableBuilder(
      extractionTemplate,
      configurableBuilderOptions,
      outboundInputOptions,
      builderLibrary,
      stupidediConfig,
      tfx12DefinitionId,
      transactionSet,
      transactionSetSubtype
    )
  }

  return {
    configurableBuilderOptions,
    configurableBuilders: configurableBuildersData?.configurableBuilders || [],
    createConfigurableBuilder,
    ediResult,
    isLoading:
      isLoadingConfigurableBuilders ||
      isLoadingRunMutation ||
      isLoadingCreateMutation ||
      isLoadingUpdateMutation,
    jsonResult,
    runQuery,
    selectedConfigurableBuilder,
    setConfigurableBuilderOptions,
    setSelectedConfigurableBuilder,
    updateConfigurableBuilder,
    setJsonResult,
    setEdiResult,
  }
}
